import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Providers } from './providers';
import { WebLayout, PanelLayout } from '@kss/layouts';
import { RequireAuth, useSessionBootstrap } from '@kss/auth';
import { CartDrawer } from '@kss/ui';

import Home from './pages/Home';
import Landing from './pages/Landing';
import About from './pages/About';
import Contact from './pages/Contact';
import Demo from './pages/Demo';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import ShopList from './shop/ShopList';
import ProductDetail from './shop/ProductDetail';
import CartPage from './shop/CartPage';
import CheckoutPage from './shop/CheckoutPage';
import { GlobalLoginModalHost } from './components/GlobalLoginModalHost';
import { AccountLayout } from './account/AccountLayout';
import AccountOverview from './account/AccountOverview';
import AccountOrders from './account/AccountOrders';
import AccountOrderDetails from './account/AccountOrderDetails';
import AccountOrderTracker from './account/AccountOrderTracker';
import AccountInvoices from './account/AccountInvoices';
import AccountInvoiceDetail from './account/AccountInvoiceDetail';
import AccountProfile from './account/AccountProfile';
import AccountAddresses from './account/AccountAddresses';
import AccountPaymentMethods from './account/AccountPaymentMethods';
import AccountWishlist from './account/AccountWishlist';
import AccountNotifications from './account/AccountNotifications';

// Full backoffice tree mirrored from apps/panel/src/App.tsx so the web
// shell can serve every /panel/* link the sidebar advertises.
import { renderPanelRoutes } from './panelRoutes';
import { ScrollToTop } from './components/ScrollToTop';

function SessionBootstrap() {
  useSessionBootstrap();
  return null;
}

export default function App() {
  return (
    <Providers>
      <BrowserRouter>
        <SessionBootstrap />
        <ScrollToTop />
        <Routes>
          {/* PUBLIC WEB */}
          <Route element={<WebLayout />}>
            {/* Shop is the home page; the marketing landing lives at /landing. */}
            <Route path="/" element={<ShopList />} />
            <Route path="/home" element={<Home />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/demo" element={<Demo />} />
            <Route path="/login" element={<Login />} />
            {/* Password-reset landing — opened from the email link
                with `?token=...`. Public route; the auth check is
                the token itself. */}
            <Route path="/reset-password" element={<ResetPassword />} />
            {/* Email-verification landing — same single-use-token
                pattern, posted to `/auth/verify-email`. */}
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/shop" element={<ShopList />} />
            <Route path="/shop/:id" element={<ProductDetail />} />
            <Route path="/cart" element={<CartPage />} />
            <Route
              path="/checkout"
              element={
                <RequireAuth redirectTo="/login">
                  <CheckoutPage />
                </RequireAuth>
              }
            />
            {/* /sample redirects to panel components showcase */}
            <Route path="/sample" element={<Navigate to="/panel/components" replace />} />

            {/* CUSTOMER ACCOUNT — public-web-themed dashboard for end users */}
            <Route
              path="/account"
              element={
                <RequireAuth redirectTo="/login">
                  <AccountLayout />
                </RequireAuth>
              }
            >
              <Route index element={<AccountOverview />} />
              <Route path="orders" element={<AccountOrders />} />
              <Route path="track-order" element={<AccountOrderTracker />} />
              <Route path="orders/:id" element={<AccountOrderDetails />} />
              {/* SSO ISSUE 4 — customer-facing invoices reuse the
                  panel's `InvoiceDocument` so the print-ready layout
                  stays identical across roles. */}
              <Route path="invoices" element={<AccountInvoices />} />
              <Route path="invoices/:id" element={<AccountInvoiceDetail />} />
              <Route path="profile" element={<AccountProfile />} />
              <Route path="addresses" element={<AccountAddresses />} />
              <Route path="payment-methods" element={<AccountPaymentMethods />} />
              <Route path="wishlist" element={<AccountWishlist />} />
              <Route path="notifications" element={<AccountNotifications />} />
            </Route>
          </Route>

          {/* AUTHENTICATED PANEL (same domain, /panel/*) */}
          <Route
            path="/panel"
            element={
              <RequireAuth redirectTo="/login">
                <PanelLayout />
              </RequireAuth>
            }
          >
            {renderPanelRoutes()}
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <GlobalLoginModalHost />
        <CartDrawer />
      </BrowserRouter>
    </Providers>
  );
}
